/**
 * Wd delivery options service (wd-delivery-options.service.ts).
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.61.0 OAUI-651 Show and hide shipping options based on status
 * 4.52.0 OAUI-603 Improve order details layout with the addition of the shipping loading zone field
 * 4.43.0 OAUI-563 Order details: add support for mongo based shipping options
 * 4.33.0 OAUI-468 Remove duplicate calls for constants and hostConfiguration
 * 4.26.0 OAUI-420 Build Basic Order Management Layout
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2021-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WdLocalSharedFunctionsService } from '../../../shared/wd-local-shared-functions.service';
import { OrderShippingTypeStatus } from '../../../../interfaces/statuses/order-status-interfaces';
import { IShippingOptionApiModel } from '../../../../interfaces/orders/order-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdOrderShippingOptionsService {
  // Booleans
  isLoading = new BehaviorSubject(false);

  // Shipping Options
  shippingOptionsActive = new BehaviorSubject<Array<IShippingOptionApiModel>>(
    [],
  );
  shippingOptionsArchived = new BehaviorSubject<Array<IShippingOptionApiModel>>(
    [],
  );

  /**
   * Constructor
   *
   * @since 4.26.0
   */
  constructor(
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
    @Inject('wdSharedFunctionsService') public wdSharedFunctionsService: any,
  ) {}

  /**
   * GET Order Shipping Status Type Key
   * Returns enum OrderShippingTypeStatus
   * @since 4.61.0
   */
  get orderShippingTypeStatusKey(): typeof OrderShippingTypeStatus {
    return OrderShippingTypeStatus;
  }

  /**
   * GET shipping options
   *
   * @since 4.61.0
   * @param parameters
   * @memberof WdOrderShippingOptionsService
   */
  async getShippingOptions(parameters?: any): Promise<any> {
    // Toggle Loader
    this.isLoading.next(true);
    try {
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.orderApiUrl}orders/shipping/types` +
          `?${new URLSearchParams(parameters)}`,
      );
      const options: any = this.parseShippingOptions(response.data);
      this.shippingOptionsActive.next(this.getActiveShippingOptions(options));
      this.shippingOptionsArchived.next(
        this.getArchivedShippingOptions(options),
      );
      return this.shippingOptionsActive.value;
    } catch (error) {
      this.wdErrorService.userError('Error', `Error getting shipping options`);
      throw error;
    } finally {
      // Toggle Loader
      this.isLoading.next(false);
    }
  }

  /**
   * Get Active Shipping Options based on Status
   * Note: Any options without a status field are treated as active
   * @since 4.61.0
   */
  getActiveShippingOptions(options: Array<any>) {
    return options.filter(
      (opt: any) =>
        !opt.status || opt.status === this.orderShippingTypeStatusKey.ACTIVE,
    );
  }

  /**
   * Get Archived Shipping Options based on Status
   * @since 4.61.0
   */
  getArchivedShippingOptions(options: Array<any>) {
    return options.filter(
      (opt: any) => opt.status === this.orderShippingTypeStatusKey.ARCHIVED,
    );
  }

  /**
   * Parse Shipping Options
   * @since 4.52.0
   * @param options
   */
  parseShippingOptions(options: any): object {
    return Array.isArray(options)
      ? options.map((option: any) => {
          return this.parseShippingOption(option);
        })
      : [];
  }

  /**
   * Parse Shipping Option
   * @since 4.52.0
   * @param option
   */
  parseShippingOption(option: any): object {
    return {
      id: option.id,
      label: option.label,
      value: option.value,
      status: option.status,
    };
  }
}
